// src/redux/sagas/siteContentSaga.ts
import { call, fork, put, takeEvery, select } from "redux-saga/effects";
import { AxiosResponse } from "axios";
import { request } from "../../api/request";
import ENDPOINTS from "../../api/endpoints";
import { SiteContent } from "../../types/siteContent";
import { RootState } from "../slices/rootReducer";
import {
  updateSiteContentRequest,
  updateSiteContentSuccess,
  updateSiteContentFailure,
  fetchSiteContentRequest,
  fetchSiteContentSuccess,
  fetchSiteContentFailure,
} from "../slices/siteContentSlice";
import { setTheme } from "../slices/themeSlice";
import { extractThemeFromSiteContent } from "../../utils/themeExtractor";
import {
  getDynamicHomeWidgets,
  processWidgetsSafely,
} from "../../utils/widgetUtils";
import { getTemplateData } from "../../data/templateData";
import { shouldSkipApiCalls } from "../../utils/appConfig";
import { getTemplateIdFromUrl } from "../../utils/pathUtils";
import { isEmpty } from "../../utils/emptyStateUtils";
import { initializeAppBackgroundTasks } from "./appInitializationSaga";

// Type for request config
interface RequestConfig {
  method?: string;
  url: string;
  data?: unknown;
  params?: Record<string, unknown>;
  headers?: Record<string, string>;
  base?: string;
  requiresAuth?: boolean;
  requiresRestaurantId?: boolean;
  timeout?: number;
  contentType?: string;
  responseType?:
    | "json"
    | "text"
    | "blob"
    | "arraybuffer"
    | "document"
    | "stream";
}

function* fetchSiteContentSaga(): Generator<any, void, any> {
  try {
    // Check if we should skip API calls (preview mode)
    const skipApiCalls = shouldSkipApiCalls();

    let parsedData: SiteContent;
    let fullWebsiteConfig: any = null;

    if (skipApiCalls) {
      // Preview mode only - load template data directly without API calls
      const templateId = getTemplateIdFromUrl();
      const templateData = getTemplateData(templateId);

      // Structure the data to match SiteContent interface
      parsedData = {
        navigation: { links: [] },
        homepage: { brand: { name: "", logo: { icon: "", text: "" } } },
        widgets: templateData?.widgets || [],
        pages: templateData?.pages || {},
      } as SiteContent;
    } else {
     const restaurantId: string | null = yield select((state: RootState) =>state.restaurant.selectedRestaurant?.restaurant_id ?? null);
    if (!restaurantId) {
      yield put(fetchSiteContentFailure("Restaurant ID not found in URL"),);
      return;
     }
      localStorage.removeItem("restaurantId");
      localStorage.setItem("restaurantId", restaurantId);
      // Normal mode - make API calls
      const url = ENDPOINTS.SITE_CONTENT_VIEW_API_PATH;

      const requestConfig: RequestConfig = {
        method: "GET",
        url,
         requiresRestaurantId:false,
         headers:{
        'restaurant-id':restaurantId
      },
        base: ENDPOINTS.API_V2_BASE_URL,
      };
      const response: AxiosResponse<any> = yield call(request, requestConfig);
      // Extract from new API response format: data[0].metadata.siteContentJson
      const websiteConfig = response?.data?.data?.[0];
      parsedData = JSON.parse(
        JSON.stringify(
          websiteConfig?.metadata?.siteContentJson || { widgets: [] },
        ),
      );
      // Store website config for later use in PUT requests
      fullWebsiteConfig = websiteConfig;
      // Initialize background tasks immediately after response (non-blocking)
      yield fork(initializeAppBackgroundTasks, parsedData);
    }

    // Only process widgets if parsedData has valid content
    if (parsedData && !isEmpty(parsedData.widgets)) {
      // Process dynamic widgets
      const safeWidgets = processWidgetsSafely(parsedData.widgets);
      const { navWidgets, homeWidgets, menuWidgetTypes } =
        getDynamicHomeWidgets(safeWidgets);

      // Update parsedData with filtered widgets
      parsedData.homeWidgets = homeWidgets;
      parsedData.navWidgets = navWidgets;
      parsedData.menuWidgetTypes = menuWidgetTypes;
    }

    // Always dispatch success with both site content and website config
    yield put(
      fetchSiteContentSuccess({
        siteContent: parsedData,
        websiteConfig: skipApiCalls ? null : fullWebsiteConfig,
      }),
    );

    // Extract and dispatch theme data from JSON structure using clean extractor
    try {
      const extractedTheme = extractThemeFromSiteContent(parsedData);

      // Dispatch theme update with font support
      yield put(
        setTheme({
          fontKey: extractedTheme.fontKey,
          primaryColor: extractedTheme.primaryColor,
        }),
      );
    } catch (themeError) {
      console.warn("Theme extraction failed:", themeError);
    }
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : "Failed to fetch site content";
    yield put(fetchSiteContentFailure(errorMessage));
  }
}

function* updateSiteContentSaga(): Generator<any, void, any> {
  if (shouldSkipApiCalls()) {
    return;
  }

  try {
    const siteContent: SiteContent | null = yield select(
      (state: RootState) => state.siteContent.data,
    );

    const websiteConfig: any = yield select(
      (state: RootState) => state.siteContent.websiteConfig,
    );

    if (!siteContent) {
      yield put(
        updateSiteContentFailure("No site content available to update"),
      );
      return;
    }

    // Clone Redux data so original state is untouched
    const apiSiteContent = structuredClone(siteContent);

    // Clear only fields that should not go in PUT payload
    delete apiSiteContent.homeWidgets;
    delete apiSiteContent.navWidgets;
    delete apiSiteContent.menuWidgetTypes;

    // Popular widget cleanup
    if (Array.isArray(apiSiteContent.widgets)) {
      apiSiteContent.widgets.forEach((widget: any) => {
        if (widget?.type?.toLowerCase().includes("popular") && widget?.data) {
          widget.data.menuData = [];
        }
      });
    }

    const payload = {
      serviceType: websiteConfig?.serviceType || "web",
      domain: websiteConfig?.domain || "",
      enabledDelivery: websiteConfig?.metadata?.enabledDelivery ?? false,
      enabledCart: websiteConfig?.metadata?.enabledCart ?? true,
      siteContentJson: apiSiteContent,
      isActive: websiteConfig?.isActive ?? true,
    };
        const restaurantId: string | null = yield select((state: RootState) =>state.restaurant.selectedRestaurant?.restaurant_id ?? null);
        if (!restaurantId) {
        yield put(
         updateSiteContentFailure("Restaurant ID not found in URL"),);
         return;
         }
      localStorage.removeItem("restaurantId");
      localStorage.setItem("restaurantId", restaurantId);

    const updateRequestConfig: RequestConfig = {
      method: "PUT",
      url: ENDPOINTS.SITE_CONTENT_UPDATE_API_PATH,
      data: payload,
      requiresRestaurantId:false,
      headers:{
        'restaurant-id':restaurantId
      },
      base: ENDPOINTS.API_V2_BASE_URL,
    };

    // 1. Update API
    yield call(request, updateRequestConfig);

    // 2. Fetch latest data after update
    yield call(fetchSiteContentSaga);

    // 3. Update completed
    yield put(updateSiteContentSuccess());
  } catch (error: unknown) {
    console.error("UPDATE SITE CONTENT ERROR:", error);
    const errorMessage =
      error instanceof Error ? error.message : "Failed to update site content";

    yield put(updateSiteContentFailure(errorMessage));
  }
}

export function* watchSiteContentSaga() {
  yield takeEvery(fetchSiteContentRequest.type, fetchSiteContentSaga);
  yield takeEvery(updateSiteContentRequest.type, updateSiteContentSaga);
}
